import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Link2, Download, Copy, Share2, Settings, Calendar, Lock, Eye, EyeOff, Upload, Palette, ArrowLeft, ExternalLink, Plus, BarChart3, Edit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { createDynamicQR, DynamicQRRow, updateDynamicQR } from '../../services/sbDynamicQR';
import { trackEvent } from '../analytics/GoogleAnalytics';
import {
  generateCustomizedDynamicQR,
  fileToDataUrl,
  validateImageFile,
  downloadDynamicQRCodePNG,
  downloadDynamicQRCodeSVG,
  DynamicQRCustomization,
  GeneratedQRResult
} from '../../utils/dynamicQRHelpers';
import { DynamicQREditModal } from './DynamicQREditModal';

export const DynamicQRGenerator: React.FC = () => {
  const [formData, setFormData] = useState({
    originalUrl: '',
    title: '',
    description: '',
    expiresAt: '',
    password: '',
  });
  
  const [customization, setCustomization] = useState<DynamicQRCustomization>({
    size: 300,
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    errorCorrectionLevel: 'M',
    transparentBackground: false,
    logoFile: null,
    logoDataUrl: null,
    margin: 4,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCustomization, setShowCustomization] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedQR, setGeneratedQR] = useState<DynamicQRRow | null>(null);
  const [generatedQRResult, setGeneratedQRResult] = useState<GeneratedQRResult | null>(null);
  const [previewQR, setPreviewQR] = useState<GeneratedQRResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleCustomizationChange = (field: keyof DynamicQRCustomization, value: any) => {
    setCustomization(prev => ({ ...prev, [field]: value }));
    // Update preview when customization changes - only need URL for preview
    if (formData.originalUrl) {
      updatePreview();
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      setCustomization(prev => ({
        ...prev,
        logoFile: file,
        logoDataUrl: dataUrl,
        // Increase error correction when logo is added
        errorCorrectionLevel: prev.errorCorrectionLevel === 'L' ? 'M' : prev.errorCorrectionLevel
      }));
      setError(null);
    } catch (err) {
      setError('Failed to process logo file');
    }
  };

  const removeLogo = () => {
    setCustomization(prev => ({
      ...prev,
      logoFile: null,
      logoDataUrl: null
    }));
  };

  // Generate preview QR code
  const updatePreview = useCallback(async () => {
    if (!formData.originalUrl) return;

    try {
      // Use the original URL for preview, or a placeholder if title is missing
      const previewUrl = formData.originalUrl;
      const result = await generateCustomizedDynamicQR(previewUrl, customization);
      setPreviewQR(result);
    } catch (err) {
      console.error('Preview generation error:', err);
    }
  }, [formData.originalUrl, customization]);

  // Update preview when form data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updatePreview();
    }, 300); // Debounce preview updates

    return () => clearTimeout(timeoutId);
  }, [formData.originalUrl, customization, updatePreview]);

  const validateForm = (): boolean => {
    if (!formData.originalUrl.trim()) {
      setError('Original URL is required');
      return false;
    }
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }

    try {
      new URL(formData.originalUrl);
    } catch {
      setError('Please enter a valid URL');
      return false;
    }

    if (formData.expiresAt && new Date(formData.expiresAt) <= new Date()) {
      setError('Expiration date must be in the future');
      return false;
    }

    return true;
  };

  const generateShortCode = () => {
    return Math.random().toString(36).substring(2, 8);
  };

  const handleGenerate = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      // Generate a short code
      const shortCode = generateShortCode();
      const shortUrl = `${window.location.origin}/r/${shortCode}`;
      
      // Generate the customized QR code with the short URL
      const qrResult = await generateCustomizedDynamicQR(shortUrl, customization);
      setGeneratedQRResult(qrResult);

      // Save to Supabase
      const result = await createDynamicQR({
        short_code: shortCode,
        original_url: formData.originalUrl,
        title: formData.title,
        description: formData.description || undefined,
        expires_at: formData.expiresAt || undefined,
        password: formData.password || undefined,
        customization: {
          ...customization,
          qrImageData: qrResult.pngDataUrl, // Store the customized QR image
        },
      });
      
      setGeneratedQR(result);
      
      trackEvent('create', 'Dynamic QR Code', 'Generated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create QR code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyShortUrl = async () => {
    if (generatedQR) {
      try {
        const shortUrl = `${window.location.origin}/r/${generatedQR.short_code}`;
        await navigator.clipboard.writeText(shortUrl);
        alert('Short URL copied to clipboard!');
        trackEvent('copy', 'Dynamic QR Code', 'Short URL');
      } catch {
        alert('Failed to copy URL');
      }
    }
  };

  const handleShare = async () => {
    if (generatedQR && navigator.share) {
      try {
        const shortUrl = `${window.location.origin}/r/${generatedQR.short_code}`;
        await navigator.share({
          title: `Dynamic QR Code: ${generatedQR.title}`,
          text: `Check out this QR code: ${generatedQR.title}`,
          url: shortUrl,
        });
        trackEvent('share', 'Dynamic QR Code', 'Native Share');
      } catch {
        // Fallback to copy
        handleCopyShortUrl();
      }
    } else {
      handleCopyShortUrl();
    }
  };

  const downloadQRCode = (format: 'png' | 'svg') => {
    if (!generatedQR || !generatedQRResult) return;

    const filename = `dynamic-qr-${generatedQR.short_code}`;

    if (format === 'png') {
      downloadDynamicQRCodePNG(generatedQRResult.pngDataUrl, filename);
    } else {
      downloadDynamicQRCodeSVG(generatedQRResult.svgString, filename);
    }

    trackEvent('download', 'Dynamic QR Code', format.toUpperCase());
  };

  const handleSaveEdit = async (updatedData: any) => {
    if (!generatedQR) return;

    try {
      // Generate updated QR code with the new data
      const shortUrl = `${window.location.origin}/r/${generatedQR.short_code}`;
      const updatedQrResult = await generateCustomizedDynamicQR(shortUrl, updatedData.customization);

      // Update the QR code in the database
      await updateDynamicQR(generatedQR.short_code, {
        original_url: updatedData.originalUrl,
        title: updatedData.title,
        description: updatedData.description || undefined,
        expires_at: updatedData.expiresAt || undefined,
        password: updatedData.password || undefined,
        customization: {
          ...updatedData.customization,
          qrImageData: updatedQrResult.pngDataUrl,
        },
      });

      // Update local state
      setGeneratedQR(prev => prev ? { ...prev, ...updatedData } : null);
      setGeneratedQRResult(updatedQrResult);
      setShowEditModal(false);

      trackEvent('edit', 'Dynamic QR Code', 'Updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update QR code');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="space-y-4">
        <Link to="/dynamic" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My QR Codes
        </Link>
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <Link2 className="inline h-10 w-10 mr-3 text-blue-600" />
            Create Dynamic QR Code
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create dynamic QR codes with live preview and customization. Change the destination URL 
            anytime without regenerating the QR code - perfect for marketing campaigns and business cards.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle>QR Code Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Original URL *"
              placeholder="https://example.com"
              value={formData.originalUrl}
              onChange={(e) => handleInputChange('originalUrl', e.target.value)}
              error={error && error.includes('URL') ? error : undefined}
            />

            <Input
              label="Title *"
              placeholder="My Dynamic QR Code"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={error && error.includes('Title') ? error : undefined}
            />

            <div>
              <label className="block text-sm font-medium mb-2">Description (Optional)</label>
              <textarea
                placeholder="Brief description of this QR code"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full p-2 border border-input rounded-md bg-background min-h-[80px]"
                maxLength={500}
              />
              <p className="text-sm text-muted-foreground mt-1">
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Customization Options */}
            <Button
              variant="outline"
              onClick={() => setShowCustomization(!showCustomization)}
              className="w-full"
            >
              <Palette className="h-4 w-4 mr-2" />
              {showCustomization ? 'Hide' : 'Show'} QR Customization
            </Button>

            {showCustomization && (
              <div className="space-y-4 p-4 border rounded-md bg-muted/50">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Logo (Optional)</label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="flex items-center space-x-2 px-4 py-2 border border-input rounded-md cursor-pointer hover:bg-accent transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      <span>Upload Logo</span>
                    </label>
                    {customization.logoFile && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={removeLogo}
                      >
                        Remove Logo
                      </Button>
                    )}
                  </div>
                  {customization.logoDataUrl && (
                    <div className="mt-2">
                      <img
                        src={customization.logoDataUrl}
                        alt="Logo preview"
                        className="w-16 h-16 object-contain border rounded"
                      />
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">
                    Max 2MB. JPEG, PNG, GIF, or WebP format.
                  </p>
                </div>

                {/* Size and Error Correction */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Size</label>
                    <input
                      type="range"
                      min="200"
                      max="800"
                      value={customization.size}
                      onChange={(e) => handleCustomizationChange('size', parseInt(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-sm text-muted-foreground">{customization.size}px</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Error Correction</label>
                    <select
                      value={customization.errorCorrectionLevel}
                      onChange={(e) => handleCustomizationChange('errorCorrectionLevel', e.target.value)}
                      className="w-full p-2 border border-input rounded-md bg-background"
                    >
                      <option value="L">Low (7%)</option>
                      <option value="M">Medium (15%)</option>
                      <option value="Q">Quartile (25%)</option>
                      <option value="H">High (30%)</option>
                    </select>
                  </div>
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Foreground Color</label>
                    <input
                      type="color"
                      value={customization.foregroundColor}
                      onChange={(e) => handleCustomizationChange('foregroundColor', e.target.value)}
                      className="w-full h-10 border border-input rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <input
                      type="color"
                      value={customization.backgroundColor}
                      onChange={(e) => handleCustomizationChange('backgroundColor', e.target.value)}
                      className="w-full h-10 border border-input rounded-md"
                      disabled={customization.transparentBackground}
                    />
                  </div>
                </div>

                {/* Transparent Background */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="transparent"
                    checked={customization.transparentBackground}
                    onChange={(e) => handleCustomizationChange('transparentBackground', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="transparent" className="text-sm font-medium">
                    Transparent Background
                  </label>
                </div>

                {/* Margin */}
                <div>
                  <label className="block text-sm font-medium mb-2">Margin</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={customization.margin}
                    onChange={(e) => handleCustomizationChange('margin', parseInt(e.target.value))}
                    className="w-full"
                  />
                  <span className="text-sm text-muted-foreground">{customization.margin} modules</span>
                </div>
              </div>
            )}

            {/* Advanced Options */}
            <Button
              variant="outline"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full"
            >
              <Settings className="h-4 w-4 mr-2" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </Button>

            {showAdvanced && (
              <div className="space-y-4 p-4 border rounded-md bg-muted/50">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <Input
                    label="Expiration Date (Optional)"
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={(e) => handleInputChange('expiresAt', e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Lock className="h-4 w-4" />
                  <div className="flex-1">
                    <Input
                      label="Password Protection (Optional)"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="mt-6"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>

              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <Button
              onClick={handleGenerate}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Generating...' : 'Generate Dynamic QR Code'}
            </Button>
          </CardContent>
        </Card>

        {/* Preview/Result */}
        <Card>
          <CardHeader>
            <CardTitle>
              {generatedQR ? 'Your Dynamic QR Code' : previewQR ? 'Live QR Code Preview' : 'QR Code Preview'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center space-y-6">
            {generatedQR && generatedQRResult ? (
              <>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <img
                    src={generatedQRResult.pngDataUrl}
                    alt="Dynamic QR Code"
                    className="max-w-full h-auto"
                    style={{ maxWidth: '300px' }}
                  />
                </div>

                <div className="w-full space-y-3 text-center">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-800 font-medium">✅ Dynamic QR Code Created!</p>
                    <p className="text-green-600 text-sm mt-1">
                      Short Code: <code className="bg-green-100 px-1 rounded">{generatedQR.short_code}</code>
                    </p>
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-left">
                    <p className="text-blue-800 font-medium text-sm">Short URL:</p>
                    <p className="text-blue-600 text-sm break-all font-mono bg-blue-100 p-2 rounded mt-1">
                      {`${window.location.origin}/r/${generatedQR.short_code}`}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-center">
                    <Button onClick={() => downloadQRCode('png')} size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      PNG
                    </Button>
                    <Button onClick={() => downloadQRCode('svg')} variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      SVG
                    </Button>
                    <Button onClick={handleCopyShortUrl} variant="outline" size="sm">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy URL
                    </Button>
                    <Button onClick={handleShare} variant="outline" size="sm">
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">
                      Manage your dynamic QR code:
                    </p>
                    <div className="flex gap-2 justify-center flex-wrap">
                      <Link to="/dynamic">
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View All QR Codes
                        </Button>
                      </Link>
                      <Link to={`/dynamic/${generatedQR.short_code}/analytics`}>
                        <Button variant="outline" size="sm">
                          <BarChart3 className="h-4 w-4 mr-2" />
                          View Analytics
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowEditModal(true)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Content
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          // Reset form for creating another QR code
                          setFormData({
                            originalUrl: '',
                            title: '',
                            description: '',
                            expiresAt: '',
                            password: '',
                          });
                          setGeneratedQR(null);
                          setGeneratedQRResult(null);
                          setPreviewQR(null);
                          setShowEditModal(false);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Another
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : previewQR && formData.originalUrl ? (
              <>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-dashed border-gray-300">
                  <img
                    src={previewQR.pngDataUrl}
                    alt="QR Code Preview"
                    className="max-w-full h-auto"
                    style={{ maxWidth: '300px' }}
                  />
                </div>
                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Live Preview - Customize your QR code above
                  </p>
                  
                  {/* Quick Download Buttons */}
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Button 
                      onClick={() => downloadDynamicQRCodePNG(previewQR.pngDataUrl, `qr-preview-${Date.now()}`)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button 
                      onClick={() => downloadDynamicQRCodeSVG(previewQR.svgString, `qr-preview-${Date.now()}`)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download SVG
                    </Button>
                  </div>
                  
                  {!formData.title ? (
                    <p className="text-sm text-orange-600">
                      Add a title to save this as a dynamic QR code
                    </p>
                  ) : (
                    <p className="text-sm text-green-600">
                      Click "Generate Dynamic QR Code" below to save and track analytics
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <Link2 className="h-16 w-16 mb-4 text-gray-300" />
                <p>Enter a URL to see your QR code preview</p>
                <p className="text-sm mt-2">
                  Dynamic QR codes can be edited anytime without changing the code itself
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      {generatedQR && (
        <DynamicQREditModal
          qrCode={generatedQR}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
};


